// @ts-nocheck
import { Storage } from "aws-amplify";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PositionImage, UserOrder } from "../interfaces";

import {
  calculateImageV2,
  getCleanFileName,
  //getRemainingCount,
  round,
} from "../utils";
import useOrder from "./useOrder";

type PageAllocation = {
  page: number;
  possiblePositions: number[];
  dpi: number;
  ratio: {
    x: number;
    y: number;
    fit: boolean;
  };
};

export interface S3ProviderListOutputItem {
  key: string;
  eTag?: string;
  lastModified?: Date;
  size?: number;
}

export const isPDF = (file: File) => {
  if (/pdf|epub/.test(file.type)) {
    return true;
  }
  return false;
};

type HandleS3Props = {
  data?: UserOrder;
};

type TValidateUpload = {
  file: File;
  intendedPositionIndex: number;
};

function useHandleS3({ data }: HandleS3Props): {
  handleS3Delete: (key: string, positionIndex: number) => Promise<void>;
  validateUpload: (props: TValidateUpload) => boolean;
  onComplete: (updated: PositionImage, positionImageIndex?: number) => Promise<any>;
  listImagesByOrderId: S3ProviderListOutputItem[];
} {
  const { t } = useTranslation();
  const { handleOrderUpdate } = useOrder();
  const [listImagesByOrderId, setListImagesByOrderId] = React.useState<
    S3ProviderListOutputItem[]
  >([]);

  const listImageS3Storage = async () => {
    if (!data) return;
    const getList = await Storage.list(data.id);
    setListImagesByOrderId(getList as S3ProviderListOutputItem[]);
  };

  React.useEffect(() => {
    listImageS3Storage();
  }, [data]);

  const getKeyName = (fileName: string) => {
    return `${Date.now()}-${fileName}`;
  };

  /**
   *
   * @param indexes index number of position that are filled
   * @returns array of article numbers which are filled
   */
  const getFilledArticleNumber = (indexes: number[]) => {
    if (!data) return;
    return data.positions
      .filter((position, index) => {
        if (indexes.includes(index)) return position.articleNumber;
      })
      .map((m) => m.articleNumber);
  };

  const validateUpload = async ({
    file,
    intendedPositionIndex,
  }: TValidateUpload): Promise<{
    key: string;
    positionImage: PositionImage;
    positionIndex: number;
    filledPositions: number[];
  } | null> => {
    const fileName: string = getCleanFileName(
      getKeyName(file.name)
    );
    if (!data || (data && !data.positions)) return Promise.resolve(null);
    if (isPDF(file)) {
      const loadedPdf = await pdfjs.getDocument(URL.createObjectURL(file))
        .promise;

      // iterate through all pages and assign possible positions
      const pageWiseAllocation = [];
      for (let i = 1; i <= loadedPdf.numPages; i++) {
        const page = await loadedPdf?.getPage(i);
        const viewport = page.getViewport({ scale: 1 });
        const width = round(viewport.width, 0);
        const height = round(viewport.height, 0);
        const pageAllocation: PageAllocation = {
          page: i,
          possiblePositions: [],
          dpi: 0,
          ratio: { x: 0, y: 0, fit: false },
        };
        for (const index in data.positions || []) {
          const { _ratio, _dpi } = calculateImageV2({
            width,
            height,
            item: data.positions[Number(index) || 0],
          });
          if (!_dpi || !_ratio || !file || !_ratio?.fit) {
            // enable continue and remove pageAllocation for this if but leave in else
            //continue;
            pageAllocation.dpi = _dpi;
            pageAllocation.ratio = _ratio;
            pageAllocation.possiblePositions.push(Number(index));
          } else {
            pageAllocation.dpi = _dpi;
            pageAllocation.ratio = _ratio;
            pageAllocation.possiblePositions.push(Number(index));
          }
        }
        pageWiseAllocation.push(pageAllocation);
      }
      // check if any of page does not have valid positions

      // Uncomment to enable dpi,ratio check

      // if (pageWiseAllocation.find((p) => p.possiblePositions.length === 0)) {
      //   toast.error(t("invalidPdfPage"));
      //   return Promise.resolve(null);
      // }
      // each page has a position. for now let's assign to first position
      const filledPositions = pageWiseAllocation
        .map((val) => val.possiblePositions)
        .flat(2);
      const mainKey = `${data.id}/${fileName}`;
      return {
        key: mainKey,
        positionImage: {
          key: mainKey,
          pdfKey: mainKey,
          thumbnail: `${data.id}/${fileName.split(".")[0]}x300.jpg`,
          size: file.size ?? 0,
          quantity: loadedPdf.numPages,
          // dpi: pageWiseAllocation[0].dpi,
          // aspectRatio: pageWiseAllocation[0].ratio,
          filledPositionArticleNumber: getFilledArticleNumber(filledPositions),
        },
        positionIndex: intendedPositionIndex,
        filledPositions: [intendedPositionIndex],
      };
    } else {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          let positionImageIndex = -1;
          let currentUpdated: PositionImage | null = null;
          const possiblePositions: number[] = [];
          for (const index in data.positions || []) {
            //const { _ratio, _dpi } = calculateImageV2({
            calculateImageV2({
              width,
              height,
              item: data.positions[Number(index) || 0],
            });

            positionImageIndex = Number(index);
            currentUpdated = {
              key: `${data.id}/${fileName}`,
              pdfKey: undefined,
              thumbnail: `${data.id}/x300-${fileName}`,
              size: file.size ?? 0,
              quantity: 1,
              // dpi: _dpi,
              // aspectRatio: _ratio,
            };
            possiblePositions.push(Number(index));
          }
          if (positionImageIndex === -1) {
            toast.error(t("imageDoesNotMatch"));
            return resolve(null);
          }
          if (currentUpdated) {
            return resolve({
              key: `${data.id}/${fileName}`,
              positionImage: {
                ...currentUpdated,
                filledPositionArticleNumber:
                  getFilledArticleNumber(possiblePositions),
              },
              positionIndex: intendedPositionIndex,
              filledPositions: [intendedPositionIndex],
            });
          }
          return resolve(null);
        };
      });
    }
  };

  const handleS3Delete = (item: PositionImage) => {
    const removePromises = [Storage.remove(item.key)];
    if (item.thumbnail) {
      removePromises.push(Storage.remove(item.thumbnail));
    }
    if (item.pdfKey) {
      removePromises.push(Storage.remove(item.pdfKey));
    }
    return removePromises;
  };

  // const checkRemainingPosition = () => {
  //   if (!data || (data && !data.positions)) return;
  //   for (let i = 0; i < data.positions.length; i++) {
  //     const position = data.positions[i];
  //     const remainingCount = getRemainingCount(position);
  //     if (remainingCount) {
  //       break;
  //     } else if (i === data.positions.length - 1) {
  //       toast.error(t("positionsCompleted"));
  //       return;
  //     }
  //   }
  //   const positionImageIndex: number[] = [];
  //   for (let index = 0; index < data?.positions.length; index++) {
  //     const position = data.positions[index];
  //     if (!position.positionImage) {
  //       position.positionImage = [];
  //     }
  //     if (
  //       position.positionImage &&
  //       position.positionImage?.length < position.quantity
  //     ) {
  //       positionImageIndex.push(index);
  //     } else {
  //       continue;
  //     }
  //   }
  //   return positionImageIndex;
  // };

  const onComplete = async (
    updated: PositionImage,
    positionImageIndex: number = 0
  ) => {
    if (!data) return;
    const images = data.positions[positionImageIndex].positionImage || [];
    const positionImage: PositionImage[] = [...images, updated];
    const position = { ...data.positions[positionImageIndex], positionImage };
    data.positions[positionImageIndex] = position;
    const updatedOrder = await handleOrderUpdate({
      id: data.id,
      positions: data.positions,
    });
    return updatedOrder;
  };

  return {
    handleS3Delete,
    validateUpload,
    onComplete,
    listImagesByOrderId,
  };
}

export default useHandleS3;
